import React from 'react';
import {Box, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import EditorCheckboxCell from '@contentlab/components/feature-editor/components/editor-checkbox-cell';
import {Resolution} from '@contentlab/types/resolution';
import translate from '@/utils/translate';

const RESOLUTIONS = [
  {
    value: Resolution['720p'],
    label: 'HD',
    subtitle: '720p',
    descriptionLocaleKey: 'For mobile and quick uploads',
    icon: (disabled: boolean) => (
      <Icon
        name="Social-InstagramReels"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: Resolution['1080p'],
    label: 'Full HD',
    subtitle: '1080p',
    descriptionLocaleKey: 'Standard quality for all platforms',
    icon: (disabled: boolean) => (
      <Icon
        name="Social-InstagramReels"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: Resolution['1440p'],
    label: '2K',
    subtitle: '1440p',
    descriptionLocaleKey: 'Sharper videos for large displays',
    icon: (disabled: boolean) => (
      <Icon
        name="Social-InstagramReels"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: Resolution['2160p'],
    label: '4K',
    subtitle: '2160p',
    descriptionLocaleKey: 'Ultra high quality for premium branding',
    badgeContent: 'Beta',
    icon: (disabled: boolean) => (
      <Icon
        name="Social-InstagramReels"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
];

const ChooseResolutionsContainer = styled(Box)(() => ({
  width: '100%',
  padding: '24px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  height: '100%',
}));

const ResolutionGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(2, 1fr)',
  gap: '20px',
  width: '100%',
  height: '80%',
}));

interface ChooseResolutionsProps {
  selected: Resolution[];
  onSelectionChange: (selected: Resolution[]) => void;
  disabledResolutions: Resolution[];
  tooltip: string;
}

function ChooseResolutions({
  selected,
  onSelectionChange,
  disabledResolutions,
  tooltip,
}: ChooseResolutionsProps) {
  const onCellClick = (key: Resolution) => {
    if (selected.includes(key)) {
      onSelectionChange(selected.filter((k) => k !== key));
    } else {
      onSelectionChange([...selected, key]);
    }
  };

  return (
    <ChooseResolutionsContainer>
      <Headline
        size="md"
        color={designSystemToken('semantic.fg.primary')}
        sx={{width: '100%', textAlign: 'left'}}
      >
        {translate('Select resolutions')}
      </Headline>
      <ResolutionGrid>
        {RESOLUTIONS.map((resolution) => {
          const disabled = disabledResolutions.includes(resolution.value);
          return (
            <EditorCheckboxCell
              isDisabled={disabled}
              key={resolution.value}
              isSelected={selected.includes(resolution.value) && !disabled}
              onClick={() => onCellClick(resolution.value)}
              icon={resolution.icon(disabled)}
              label={resolution.label}
              subtitle={resolution.subtitle}
              badgeContent={resolution.badgeContent ?? null}
              description={translate(resolution.descriptionLocaleKey)}
              tooltip={tooltip}
            />
          );
        })}
      </ResolutionGrid>
    </ChooseResolutionsContainer>
  );
}

export default ChooseResolutions;
